// Inline volleyball set-score entry: one points pair per set (best-of-5 max),
// sets-won derived live via the SAME shared helper the server trusts
// (@staty/shared/utils/setScores), so the saved homeScore/awayScore can never
// disagree with the sets. Non-volleyball sports keep ManualScoreEntry.
import React, { useState } from 'react';
import { useAuth } from '@/auth/clerkShim';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Plus, X } from 'lucide-react';
import useGameStore from '@/store/gameStore';
import { deriveSetsWon } from '@staty/shared/utils/setScores';

interface SetPair {
  home: string;
  away: string;
}

const MAX_SETS = 5;

const toPairs = (setScores?: Array<{ home?: number; away?: number }> | null): SetPair[] => {
  const pairs = (setScores || []).map((s) => ({
    home: s?.home == null ? '' : String(s.home),
    away: s?.away == null ? '' : String(s.away),
  }));
  while (pairs.length < 3) pairs.push({ home: '', away: '' });
  return pairs.slice(0, MAX_SETS);
};

const toSetScores = (pairs: SetPair[]) =>
  pairs
    .filter((p) => p.home !== '' && p.away !== '')
    .map((p) => ({ home: Number(p.home), away: Number(p.away) }));

export default function SetScoreEntry({ game, onSaved }: { game: any; onSaved?: () => void }) {
  const { getToken } = useAuth();
  const updateGame = useGameStore((s: any) => s.updateGame(getToken));
  const [pairs, setPairs] = useState<SetPair[]>(() => toPairs(game.setScores));
  const [saving, setSaving] = useState(false);

  const sets = toSetScores(pairs);
  const setsWon = deriveSetsWon(sets);
  const dirty = JSON.stringify(sets) !== JSON.stringify(toSetScores(toPairs(game.setScores)));

  const setPair = (i: number, side: keyof SetPair, value: string) => {
    setPairs((prev) => prev.map((p, idx) => (idx === i ? { ...p, [side]: value.replace(/[^0-9]/g, '') } : p)));
  };

  const save = async () => {
    if (saving || !dirty) return;
    setSaving(true);
    try {
      await updateGame({
        ...game,
        setScores: sets,
        // Sets won ARE the game score for volleyball (standings/brackets
        // read these two fields unchanged).
        homeScore: sets.length ? setsWon.home : null,
        awayScore: sets.length ? setsWon.away : null,
      });
      onSaved && onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="set-score-entry">
      {pairs.map((p, i) => (
        <div key={i} className="flex items-center gap-0.5 rounded-md border px-1 py-0.5">
          <Input
            type="text" inputMode="numeric" aria-label={`Set ${i + 1} home points`}
            className="h-7 w-10 border-0 p-0 text-center" placeholder="–" value={p.home}
            onChange={(e) => setPair(i, 'home', e.target.value)}
          />
          <span className="text-muted-foreground text-xs">:</span>
          <Input
            type="text" inputMode="numeric" aria-label={`Set ${i + 1} away points`}
            className="h-7 w-10 border-0 p-0 text-center" placeholder="–" value={p.away}
            onChange={(e) => setPair(i, 'away', e.target.value)}
          />
        </div>
      ))}
      {pairs.length < MAX_SETS && (
        <Button
          size="sm" variant="ghost" className="h-7 px-1.5" aria-label="Add set"
          onClick={() => setPairs((prev) => [...prev, { home: '', away: '' }])}
        >
          <Plus className="h-4 w-4" />
        </Button>
      )}
      {pairs.length > 3 && (
        <Button
          size="sm" variant="ghost" className="h-7 px-1.5" aria-label="Remove last set"
          onClick={() => setPairs((prev) => prev.slice(0, -1))}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      <span className="text-sm font-medium tabular-nums" data-testid="sets-won">
        {sets.length ? `${setsWon.home}–${setsWon.away}` : ''}
      </span>
      <Button size="sm" variant="secondary" onClick={save} disabled={!dirty || saving}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
      </Button>
    </div>
  );
}
